import React, { Component, MouseEvent } from 'react';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';

import { DebouncedFunc, isEqual } from 'lodash';
import { RegionDto, TruckDto } from '@trucktrax/trucktrax-ts-common';
import constants from '../../../../../constants/messagingConstants';
import AutoCompleteWithChip, { DataItem } from '../../AutoCompleteWithChip';
import { MessageCenterInfo } from '../../index.d';
import styles from './NewMessage.module.css';
import ComposeMessage from '../ComposeMessage';
import { ReduxState } from '../../../../../store';
import { isEmptyArray } from '../../../../../util/appUtil';
import { getFullName } from '../../../../../util/adminUtil';
import { PositionDetailsDto } from '../../../../../types';
import { DriverDetail } from '../../../../../store/selectors/messageSelectors';

class NewMessage extends Component<NewMessageProps, NewMessageState> {
  messageContainer: Element | null;

  composeMessageComponent: ComposeMessage | null = null;

  debounceShadowDisplay: DebouncedFunc<any>;

  constructor(props: NewMessageProps) {
    super(props);

    this.messageContainer = null;
    this.debounceShadowDisplay = debounce(this.handleShadowDisplay, 300);

    this.state = {
      dataSource: [],
      isPending: false,
      hideTopShadow: true,
      hideBottomShadow: true,
      items: [],
      selectedItems: [],
      inputValue: '',
      initiallySelected: false,
    };
  }

  async componentDidMount() {
    const { driverList: dataSource, initialSelectedItems } = this.props;
    this.setState({ dataSource }, () => {
      this.updateStateItems(initialSelectedItems);
    });
    window.addEventListener('resize', this.debounceShadowDisplay);
  }

  componentDidUpdate(prevProps: NewMessageProps) {
    const {
      messageCenter: currentMessageCenter,
      driverList: currentDriverList,
      getTruckAlias: currentGetTruckAlias,
      truckList: currentTrucks,
      positions: currentPositions,
    } = this.props;
    const {
      messageCenter: previousMessageCenter,
      driverList: previousDriverList,
      getTruckAlias: previousGetTruckAlias,
      truckList: previousTrucks,
      positions: previousPositions,
    } = prevProps;
    const partiesChanged = !isEqual(
      previousMessageCenter.partiesList,
      currentMessageCenter.partiesList,
    );
    const driverListChanged = !isEqual(previousDriverList, currentDriverList);
    const getTruckAliasChanged = !isEqual(previousGetTruckAlias, currentGetTruckAlias);
    const trucksChanged = !isEqual(previousTrucks, currentTrucks);
    const positionsChanged = !isEqual(previousPositions, currentPositions);
    const shouldUpdate =
      partiesChanged ||
      driverListChanged ||
      getTruckAliasChanged ||
      trucksChanged ||
      positionsChanged;
    if (shouldUpdate) {
      this.updateStateItems();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.debounceShadowDisplay);
  }

  onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    this.setState({ isPending: true });
    this.props.onSubmit(this.props.messageCenter, null, this.disableButtonCallback);
  };

  // method to be called to update the current recipients via changes to the store.
  updateStateItems = (initiallySelectedItems?: DataItem[]) => {
    if (initiallySelectedItems && !isEmptyArray(initiallySelectedItems)) {
      this.addAllItems(initiallySelectedItems);
      return;
    }

    const { driverList, messageCenter, selectedProductLine } = this.props;
    let stateItems: DataItem[] = driverList
      .map(driver => {
        const driverName = getFullName(driver);

        let displayName = driverName;
        if (this.props.getTruckAlias && driver.url) {
          const truckAlias = this.props.getTruckAlias(driver.url);
          if (truckAlias) {
            displayName = `${driverName} (${truckAlias})`;
          }
        }

        return {
          label: driver.label,
          fullName: displayName,
          id: driver.id,
          url: driver.url,
        };
      })
      .sort((a, b) => a.fullName.localeCompare(b.fullName));

    const productLineString =
      selectedProductLine === constants.READY_MIX_VALUE
        ? constants.READY_MIX_STRING
        : selectedProductLine;
    const label = selectedProductLine ? `All ${productLineString} Drivers` : '';
    const url = selectedProductLine || 'Product Line';
    const allDriversInRegionItem = {
      fullName: label,
      id: 1,
      url,
    };

    if (messageCenter.partiesList.length > 0) {
      messageCenter.partiesList.forEach((party: { url: string }) => {
        stateItems = stateItems.filter(driver => driver.url !== party.url);
      });
    }
    // add "add all drivers in product line" to stateItems if not all drivers have been added
    if (stateItems.length > 0) {
      stateItems.unshift(allDriversInRegionItem);
    }
    this.setState({
      items: stateItems,
    });
  };

  clearChips = () => {
    const availableItems = this.state.dataSource.slice();
    this.setState({
      inputValue: '',
      selectedItems: [],
      items: availableItems,
    });

    // clear redux state for partyList
    this.props.onToFieldChange([]);
  };

  handleMessageFieldChange = (event: any) => {
    this.props.onMessageFieldChange(event.target.value);
  };

  disableButtonCallback = () => {
    this.setState({ isPending: false });
  };

  handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const element = e.target as HTMLInputElement;
    const { className } = element;

    // This needs to match the div with the ref below
    // this.messageContainer
    // otherwise it will render the shadow when scrolling the
    // recipient suggestion list
    if (className.includes('NewMessage_newMessageContainer')) {
      this.renderShadow(element);
    }
  };

  handleShadowDisplay = () => {
    this.renderShadow(this.messageContainer!);
  };

  static handleFocus = (element: AutoCompleteWithChip) => {
    element?.popoverInputNode?.focus();
  };

  handleChangeInput = (inputVal: string) => {
    if (inputVal !== undefined) {
      const isString = !inputVal.includes('[object Object]');
      const t = inputVal.split(',');
      if (isString && JSON.stringify(t) !== JSON.stringify(this.state.selectedItems)) {
        this.props.onCurrentRecipientSearchTextChange(inputVal);
        this.setState({ inputValue: inputVal });
      }
    }
  };

  handleChange = (selectedItem: DataItem) => {
    const alreadyIncludesItem = this.state.selectedItems.some(
      sourceItem => sourceItem.url === selectedItem.url,
    );
    if (alreadyIncludesItem) {
      this.removeSelectedItem(selectedItem);
    } else {
      this.addSelectedItem(selectedItem);
    }
  };

  addSelectedItem = (item: DataItem) => {
    if (item.url === this.props.selectedProductLine) {
      this.addAllItems();
    } else {
      this.addSingleSelectedItem(item);
    }
  };

  addAllItems = (sourceItems?: DataItem[]) => {
    const stateItems = sourceItems ?? this.state.items;

    this.setState(
      {
        inputValue: '',
        items: [],
        selectedItems: stateItems,
      },
      () => {
        this.handleShadowDisplay();
        const chipSource = sourceItems ?? this.state.dataSource;
        // add item to redux state
        const chips = chipSource.map(i => ({
          label: i!.label,
          fullName: i!.fullName,
          url: i!.url,
        }));
        const firstChipUrl = chips.at(0)?.url;
        if (firstChipUrl && firstChipUrl === this.props.selectedProductLine) {
          chips.shift();
        }
        this.props.onToFieldChange(chips);
      },
    );
  };

  addSingleSelectedItem = (item: DataItem) => {
    const { selectedItems: currentSelectedItems, items } = this.state;
    const newSelectedItems = [...currentSelectedItems, item];
    const newItems = items.filter((i: DataItem) => i.url !== item.url);
    this.setState(
      {
        inputValue: '',
        selectedItems: newSelectedItems,
        items: newItems,
      },
      () => {
        this.handleShadowDisplay();

        // add item to redux state
        const itemFromDataSource = this.state.dataSource.filter(
          (i: DataItem) => i.url === item.url,
        );
        if (itemFromDataSource && isEmptyArray(itemFromDataSource)) {
          return;
        }
        const firstItem = itemFromDataSource[0];
        const chipToAdd: DataItem = {
          label: firstItem.label,
          fullName: firstItem.fullName,
          url: firstItem.url,
          id: firstItem.id,
        };
        const newChips: DataItem[] = [...this.props.messageCenter.partiesList];

        newChips.push(chipToAdd);
        this.props.onToFieldChange(newChips);
      },
    );
  };

  removeSelectedItem = (item: DataItem) => {
    this.setState(
      ({ selectedItems: selectedItem, items, dataSource }) => {
        const itemFromDataS = dataSource.filter(i => i.url === item.url);
        const firstInArr = itemFromDataS.shift() || null;

        const newItems = [...items];

        if (firstInArr?.fullName && firstInArr?.id && firstInArr?.url) {
          newItems.push(firstInArr);
        }

        return {
          inputValue: '',
          selectedItems: selectedItem.filter(i => i.url !== item.url),
          items: newItems,
        };
      },
      () => {
        this.handleShadowDisplay();

        // delete item from redux state
        const currentChips = this.props.messageCenter.partiesList;
        const newChips = currentChips.filter((i: { url: any }) => i.url !== item.url);
        this.props.onToFieldChange(newChips);
      },
    );
  };

  onToFieldTabKeyPressed = () => this.composeMessageComponent?.focusTextArea();

  renderShadow = (element: Element) => {
    if (element && element.scrollHeight - element.clientHeight !== 0) {
      if (element.scrollTop === 0) {
        this.setState({
          hideTopShadow: true,
          hideBottomShadow: false,
        });
      } else if (element.scrollTop === element.scrollHeight - element.clientHeight) {
        this.setState({
          hideTopShadow: false,
          hideBottomShadow: true,
        });
      } else {
        this.setState({
          hideTopShadow: false,
          hideBottomShadow: false,
        });
      }
    } else {
      this.setState({
        hideTopShadow: true,
        hideBottomShadow: true,
      });
    }
  };

  render() {
    const { messageFieldValue, partiesList, currentRecipientSearchText } = this.props.messageCenter;
    const chipInput = (
      <div className={styles.chipInputContainer}>
        <label htmlFor="toLabel" className={styles.label}>
          To:
        </label>
        <AutoCompleteWithChip
          ref={element => {
            if (
              element?.popoverInputNode !== undefined &&
              element?.props?.partiesList?.length === 0 &&
              this.state.initiallySelected === false
            ) {
              this.setState({ initiallySelected: true });
              if (this.props.delayDriverFocus) {
                setTimeout(() => NewMessage.handleFocus(element), 300);
              } else {
                NewMessage.handleFocus(element);
              }
            }
          }}
          data-test="message-center-to-field"
          onInputValueChange={this.handleChangeInput}
          inputValue={this.state.inputValue || currentRecipientSearchText}
          availableItems={this.state.items}
          selectedItem={this.state.selectedItems}
          onChange={this.handleChange}
          onRemoveItem={this.removeSelectedItem}
          partiesList={partiesList}
          selectedProductLine={this.props.selectedProductLine}
          onTabKeyPressed={this.onToFieldTabKeyPressed}
        />
        {partiesList.length ? (
          <button
            data-test="clear-chips-button"
            className={styles.clearChips}
            onClick={this.clearChips}
          >
            <i aria-hidden="true" className="icon-cancel" />
          </button>
        ) : null}
      </div>
    );
    const submitDisabled =
      !messageFieldValue ||
      messageFieldValue.trim() === '' ||
      partiesList.length === 0 ||
      this.state.isPending;
    const textField = (
      <ComposeMessage
        ref={element => {
          this.composeMessageComponent = element;
        }}
        value={messageFieldValue}
        onChange={this.handleMessageFieldChange}
        submitDisabled={submitDisabled}
        onSubmit={this.onSubmit}
        placeholder={constants.NEW_PLACEHOLDER}
        selectedProductLine={this.props.selectedProductLine}
        currentRegion={this.props.currentRegion}
        onMessageFieldChange={this.props.onMessageFieldChange}
      />
    );

    const spanClassName = cx(
      'txt-bold',
      styles.title,
      this.state.hideTopShadow ? styles.hideTopShadow : '',
    );

    return (
      <div className={styles.container}>
        <span className={spanClassName}>New message</span>
        <div
          data-test="new-message-container"
          className={styles.newMessageContainer}
          onScroll={this.handleScroll}
          ref={element => {
            this.messageContainer = element;
          }}
        >
          <div className={styles.chipsContainer}>
            <div className={styles.recipientsContainer}>{chipInput}</div>
          </div>
        </div>
        <div
          className={cx(
            styles.textFieldContainer,
            this.state.hideBottomShadow ? styles.hideBottomShadow : '',
          )}
        >
          {textField}
        </div>
      </div>
    );
  }
}

interface NewMessageState {
  dataSource: DataItem[];
  isPending: boolean;
  hideTopShadow: boolean;
  hideBottomShadow: boolean;
  items: DataItem[];
  selectedItems: DataItem[];
  inputValue: string;
  initiallySelected: boolean;
}

export type NewMessageProps = NewMessageOwnProps & {
  messageCenter: MessageCenterInfo;
  driverList: DriverDetail[];
  onToFieldChange: (partyMembers: DataItem[]) => void;
  onCurrentRecipientSearchTextChange: (newSearchText: string) => void;
  onMessageFieldChange: (message: string) => void;
  onSubmit: (
    messageCenter: MessageCenterInfo,
    messageId: number | null,
    callback?: () => void,
  ) => void;
  selectedProductLine?: string;
  delayDriverFocus?: boolean;
  currentRegion: RegionDto;
  initialSelectedItems?: DataItem[];
  getTruckAlias?: (driverUrl?: string) => string | undefined;
};

interface NewMessageOwnProps {
  truckList: TruckDto[];
  positions: PositionDetailsDto[];
}

function mapStateToProps(state: ReduxState) {
  return {
    initialSelectedItems: state.currentTab?.selectedItems ?? [],
    truckList: state.allTrucks,
    positions: state.positionList,
  };
}

export default connect(mapStateToProps)(NewMessage);
